import { ShippingOption } from 'API';
import Label from 'components/common/Label';
import { useInvoicingDetail } from 'providers/InvoicingDetailModuleProvider';
import { useMemo } from 'react';
import { getGroupByCarrierData } from 'shared/helpers/invoice/invoice.helper';
import { NonNullableRecord } from 'types/generic';
import { CarrierListsElement } from './CarrierListsElement/CarrierListsElement';
import { ShippingMethodElement } from './ShippingMethodElement/ShippingMethodElement';

/**
 * Props for the ContentLoaded component.
 */
interface ContentLoadedProps {
  /** The data grouped by carrier. */
  groupByCarrierData: Record<string, ShippingOption[]> | null;
}

/**
 * Component to render the carrier lists and shipping method elements.
 * @param groupByCarrierData - The data grouped by carrier.
 * @returns The CarrierElementData component.
 */
const CarrierElementData: React.FC<NonNullableRecord<ContentLoadedProps>> = ({ groupByCarrierData }) => {
  return (
    <>
      <CarrierListsElement groupByCarrierData={groupByCarrierData} />
      <ShippingMethodElement groupByCarrierData={groupByCarrierData} />
    </>
  );
};

/**
 * Component to render the content based on the grouped carrier data.
 * @param groupByCarrierData - The data grouped by carrier.
 * @returns The ContentLoaded component.
 */
const ContentLoaded: React.FC<ContentLoadedProps> = ({ groupByCarrierData }) => {
  if (!groupByCarrierData) return <div className="text-sm text-gray-500">No Carriers found.</div>;
  return <CarrierElementData groupByCarrierData={groupByCarrierData} />;
};

/**
 * Component to render the carrier element.
 * @returns The CarrierElement component.
 */
export const CarrierElement: React.FC = () => {
  const { shippingOptions, invoiceShippingInput } = useInvoicingDetail();
  const { packageType } = invoiceShippingInput;

  const groupByCarrierData = useMemo(() => {
    return getGroupByCarrierData(shippingOptions, packageType);
  }, [packageType, shippingOptions]);

  return (
    <div className="flex flex-col gap-2">
      <Label required htmlFor="carrier">
        Carrier
      </Label>
      <ContentLoaded groupByCarrierData={groupByCarrierData} />
    </div>
  );
};
